/**
 * Job Center API client — GET/POST /v1/jobs* (src/march/api/job_routes.py).
 *
 * Backs the global Job Center: list/detail/cancel/retry plus a live SSE feed
 * consumed via the shared fetch-based reader (native EventSource can't send
 * the Authorization header this API requires).
 */

import { fetchWithAuth } from './fetchWithAuth';
import { readNamedSseStream } from './sseStreamReader';
import { getMarchHttpApiBase, getMarchRuntimeBearer, isMarchHttpApiConfigured } from './marchApiEnv';

export type JobStatus =
  | 'queued'
  | 'running'
  | 'retrying'
  | 'recovering'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface JobRecord {
  job_id: string;
  tenant_id: string;
  job_type: string;
  status: JobStatus;
  priority: number;
  idempotency_key: string;
  resource_lock_key: string | null;
  payload: Record<string, unknown>;
  checkpoint: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error_message: string | null;
  error_detail: Record<string, unknown> | null;
  recovery_strategy: string;
  max_attempts: number;
  attempt_count: number;
  correlation_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  available_at: string;
  started_at: string | null;
  finished_at: string | null;
  cancel_requested: boolean;
  /** Durable acknowledgement (server-side): survives refresh, devices, re-login. */
  seen_at?: string | null;
  seen_by?: string | null;
}

/**
 * A per-step problem inside a job that still finished. The prepare pipeline is
 * deliberately fail-open per step (a broken narrative must not block organ
 * scores), so `status: 'completed'` alone never proved the clinical work
 * landed — `result.warnings` is the honest signal, and it lives here rather
 * than in a component so the context, toasts, and drawer all read it the same
 * way without importing each other.
 */
export interface PrepareWarning {
  patient_id: string;
  step: string;
  status: string;
}

/**
 * The published demo is a recorded walkthrough of finished work, so it does not
 * surface the prepare-time audit warnings: they turn the drawer into a wall of
 * "needs review" about a synthetic patient's twin build, which is noise next to
 * what the demo is actually showing.
 *
 * PRESENTATION ONLY. The captured job records still carry their warnings
 * verbatim — nothing is edited out of the data, this just does not escalate it
 * in the demo build.
 */
const DEMO_HIDE_JOB_WARNINGS =
  String(import.meta.env.VITE_DEMO_READONLY ?? '').trim().toLowerCase() === 'true';

export function jobWarnings(job: JobRecord): PrepareWarning[] {
  if (DEMO_HIDE_JOB_WARNINGS) return [];
  const raw = (job.result as Record<string, unknown> | null)?.warnings;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (w): w is PrepareWarning =>
      !!w && typeof w === 'object' && typeof (w as PrepareWarning).step === 'string',
  );
}

export function jobHasWarnings(job: JobRecord): boolean {
  return jobWarnings(job).length > 0;
}

export interface JobEvent {
  seq: number;
  event_id: string;
  job_id: string;
  tenant_id: string;
  event_type: string;
  from_status: string | null;
  to_status: string | null;
  message: string | null;
  progress_pct: number | null;
  eta_seconds: number | null;
  metadata: Record<string, unknown>;
  occurred_at: string;
}

function headers(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = (import.meta.env.VITE_COMMAND_API_KEY ?? '').trim();
  if (key) h['X-API-Key'] = key;
  const bearer = getMarchRuntimeBearer();
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

export function isJobsApiAvailable(): boolean {
  return isMarchHttpApiConfigured();
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    // FastAPI errors are {"detail": "..."} — surfacing the raw JSON put a
    // brace-wrapped blob in front of the clinician instead of the sentence the
    // endpoint wrote (e.g. why a stale build may not be retried).
    let detail = body;
    try {
      const parsed = JSON.parse(body) as { detail?: unknown };
      if (typeof parsed?.detail === 'string' && parsed.detail) detail = parsed.detail;
    } catch {
      // Not JSON — the raw text is already the best message we have.
    }
    throw new Error(detail || `${label} failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export async function fetchJobs(params: {
  status?: JobStatus;
  jobType?: string;
  limit?: number;
} = {}): Promise<JobRecord[]> {
  if (!isJobsApiAvailable()) return [];
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.jobType) qs.set('job_type', params.jobType);
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/jobs${suffix}`, {
    method: 'GET',
    extraHeaders: headers(),
  });
  const body = await parseOrThrow<{ jobs: JobRecord[] }>(res, 'list jobs');
  return body.jobs;
}

/**
 * Per-job-type queue heartbeat: depth, oldest-queued age, running count, the
 * 24h completed/failed split, and p95 run time.
 *
 * Admin-only (GET /v1/admin/jobs/queue-stats). It has existed server-side since
 * the job engine shipped and nothing consumed it -- which is how nine frozen
 * twins once stayed invisible: the metric existed, no surface read it.
 * Best-effort like the rest of this client: a 403 (non-admin) or an
 * unconfigured API yields [] rather than breaking the page around it.
 */
export interface JobQueueStat {
  job_type: string;
  queued: number;
  running: number;
  oldest_queued_age_s: number | null;
  completed_24h: number;
  failed_24h: number;
  p95_run_seconds_24h: number | null;
}

export async function fetchJobQueueStats(): Promise<JobQueueStat[]> {
  if (!isJobsApiAvailable()) return [];
  try {
    const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/admin/jobs/queue-stats`, {
      method: 'GET',
      extraHeaders: headers(),
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { types?: JobQueueStat[] };
    return body.types ?? [];
  } catch {
    return [];
  }
}

export async function fetchJob(jobId: string): Promise<{ job: JobRecord; events: JobEvent[] } | null> {
  if (!isJobsApiAvailable()) return null;
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/jobs/${encodeURIComponent(jobId)}`, {
    method: 'GET',
    extraHeaders: headers(),
  });
  if (res.status === 404) return null;
  return parseOrThrow(res, 'get job');
}

export async function cancelJob(jobId: string): Promise<{ accepted: boolean; job_id: string }> {
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: 'POST',
    extraHeaders: headers(),
  });
  return parseOrThrow(res, 'cancel job');
}

/**
 * Durable acknowledgement: stamps seen_at/seen_by server-side so "needs
 * attention" state survives refresh, devices, and re-login. Owner-scoped --
 * the server marks only jobs the caller may see. Returns the ids actually
 * marked (already-seen ids are simply absent).
 */
export async function markJobsSeen(jobIds: string[]): Promise<string[]> {
  const ids = jobIds.filter((j) => j.trim());
  if (ids.length === 0 || !isJobsApiAvailable()) return [];
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/jobs/seen`, {
    method: 'POST',
    extraHeaders: headers(),
    body: JSON.stringify({ job_ids: ids }),
  });
  const body = (await parseOrThrow(res, 'mark jobs seen')) as { marked?: string[] };
  return body.marked ?? [];
}

export async function retryJob(
  jobId: string,
): Promise<{ accepted: boolean; job_id: string; retried_from: string }> {
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/jobs/${encodeURIComponent(jobId)}/retry`, {
    method: 'POST',
    extraHeaders: headers(),
  });
  return parseOrThrow(res, 'retry job');
}

/**
 * Enqueue a synthetic test job (debug-only backend endpoint, 404s unless
 * MARCH_JOBS_SYNTHETIC_TYPE_ENABLED=1). Used for Job Center verification, not
 * a real workflow.
 */
export async function enqueueSyntheticDebugJob(payload: {
  label?: string;
  total_steps?: number;
  sleep_seconds?: number;
  fail_on_step?: number;
  fail_max_times?: number;
}): Promise<{ accepted: boolean; job_id: string | null }> {
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/admin/jobs/debug/synthetic`, {
    method: 'POST',
    extraHeaders: headers(),
    body: JSON.stringify(payload),
  });
  return parseOrThrow(res, 'enqueue synthetic job');
}

/**
 * Live job-event stream (GET /v1/jobs/stream), replayed from `lastSeq` via
 * Last-Event-Id. Runs until the caller's AbortSignal fires; on any stream
 * failure it resolves so the caller can fall back to polling. `onOpen` fires
 * once the stream is actually established (HTTP 200 with a body) — callers
 * use it to flip their "live" indicator only when the feed is really live.
 */
export async function openJobEventStream(
  onEvent: (evt: JobEvent) => void,
  opts: { lastSeq?: number; signal?: AbortSignal; onOpen?: () => void } = {},
): Promise<void> {
  if (!isJobsApiAvailable()) return;
  const extraHeaders = headers();
  if (opts.lastSeq != null) extraHeaders['Last-Event-Id'] = String(opts.lastSeq);
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/jobs/stream`, {
    method: 'GET',
    extraHeaders,
    signal: opts.signal,
  });
  if (!res.ok || !res.body) return;
  opts.onOpen?.();
  await readNamedSseStream(res, (_eventName, data) => {
    if (data && typeof data === 'object') onEvent(data as JobEvent);
  });
}
